import React, { useEffect, useState } from "react";
import { ChatMessageData, ImageReference } from "./types";
import { blobToDataUrl } from "./imageUtils";

interface UseImageReferenceReturn {
  selectedChatImageForReference: ImageReference | null;
  handleUseAsReference: (messageId: string) => Promise<void>;
  clearReference: () => void;
}

/**
 * Hook to manage reference image selection from chat messages
 * Automatically selects the last generated image as reference
 */
export const useImageReference = (messages: ChatMessageData[]): UseImageReferenceReturn => {
  const [selectedChatImageForReference, setSelectedChatImageForReference] = useState<ImageReference | null>(null);

  // Get the last generated image data for reference
  const lastGeneratedImage = React.useMemo(() => {
    const lastAiMessage = [...messages].reverse().find(msg => msg.role === "assistant" && msg.imageUrl && msg.imageBlob);
    if (!lastAiMessage) return null;
    return lastAiMessage;
  }, [messages]);

  // Auto-select last generated image as reference when it changes
  useEffect(() => {
    const convertAndSetReference = async () => {
      if (lastGeneratedImage?.imageBlob && lastGeneratedImage.imageUrl) {
        const dataUrl = await blobToDataUrl(lastGeneratedImage.imageBlob);
        setSelectedChatImageForReference({
          displayUrl: lastGeneratedImage.imageUrl,
          dataUrl,
        });
      }
    };
    convertAndSetReference();
  }, [lastGeneratedImage]);

  // Handler for when user clicks "use as reference" on a chat image
  const handleUseAsReference = async (messageId: string) => {
    const message = messages.find(msg => msg.id === messageId);
    if (!message) return;

    // For AI messages, convert blob to data URL
    if (message.role === "assistant" && message.imageUrl && message.imageBlob) {
      const dataUrl = await blobToDataUrl(message.imageBlob);
      setSelectedChatImageForReference({ displayUrl: message.imageUrl, dataUrl });
      return;
    }

    // For user messages, use the reference image they uploaded (already base64)
    if (message.role === "user" && message.referenceImageUrl) {
      setSelectedChatImageForReference({
        displayUrl: message.referenceImageUrl,
        dataUrl: message.referenceImageUrl,
      });
    }
  };

  // Handler to clear the reference image
  const clearReference = () => {
    setSelectedChatImageForReference(null);
  };

  return {
    selectedChatImageForReference,
    handleUseAsReference,
    clearReference,
  };
};
